import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import Homepage from "./pages/homepage";
import usePageMetadata from "./components/common/usePageMetadata";

import { TRACKING_ID } from "./data/tracking";
import "./app.css";

const TouchCountPage = lazy(() => import("./pages/TouchCountPage"));
const TouchCountLegal = lazy(() => import("./pages/TouchCountLegal"));

const About = lazy(() => import("./pages/about"));
const Services = lazy(() => import("./pages/services"));
const Projects = lazy(() => import("./pages/projects"));
const Contact = lazy(() => import("./pages/contact"));
const Notfound = lazy(() => import("./pages/404"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const LunexaMessagesPrivacyPolicy = lazy(
	() => import("./pages/LunexaMessagesPrivacyPolicy"),
);
const LunexaMessagesTerms = lazy(() => import("./pages/LunexaMessagesTerms"));
const ProductLandingPage = lazy(() => import("./pages/ProductLandingPage"));
const GeoQuizGamePage = lazy(() => import("./pages/GeoQuizGamePage"));
const GeoQuizLegal = lazy(() => import("./pages/GeoQuizLegal"));
const PlottoGamePage = lazy(() => import("./pages/PlottoGamePage"));
const ExternalProjectRedirect = lazy(
	() => import("./pages/ExternalProjectRedirect"),
);
const SkadNetworkMerge = lazy(() => import("./pages/skadNetworkMerge"));
const AppAdsTxtMerger = lazy(() => import("./pages/appAdsTxtMerger"));
const QuietVaultPrivacyPolicy = lazy(
	() => import("./pages/QuietVaultPrivacyPolicy"),
);
const QuietVaultTerms = lazy(() => import("./pages/QuietVaultTerms"));

const RouteMetadata = () => {
	const { pathname } = useLocation();
	usePageMetadata(undefined, undefined, { pathname });
	return null;
};

function App() {
	useEffect(() => {
		if (TRACKING_ID !== "") {
			import("react-ga4").then(({ default: ReactGA }) => {
				ReactGA.initialize(TRACKING_ID);
			});
		}
	}, []);

	return (
		<div className="App">
			<RouteMetadata />
			<Suspense
				fallback={
					<div className="route-loading" role="status">
						Loading page…
					</div>
				}
			>
				<Routes>
					<Route path="/" element={<Homepage />} />
					<Route path="/about" element={<About />} />
					<Route
						path="/software-development-lifecycle"
						element={<Services />}
					/>
					<Route path="/projects" element={<Projects />} />
					<Route path="/apps/touch-point" element={<TouchCountPage />} />
					<Route path="/apps/touch-point/terms" element={<TouchCountLegal type="terms" />} />
					<Route path="/apps/touch-point/privacy" element={<TouchCountLegal type="privacy" />} />
					<Route path="/apps/geo-quiz-guess-the-flag/privacy" element={<GeoQuizLegal type="privacy" />} />
					<Route path="/apps/geo-quiz-guess-the-flag/terms" element={<GeoQuizLegal type="terms" />} />
					{["apps", "projects"].flatMap((prefix) => ["privacy", "terms"].map((type) => (
						<Route
							key={`${prefix}-geo-legal-${type}`}
							path={`/${prefix}/geo-master-quest/${type}`}
							element={<Navigate to={`/apps/geo-quiz-guess-the-flag/${type}/`} replace />}
						/>
					)))}
					<Route
						path="/apps/geo-quiz-guess-the-flag"
						element={<GeoQuizGamePage />}
					/>
					<Route
						path="/projects/geo-master-quest"
						element={<Navigate to="/apps/geo-quiz-guess-the-flag/" replace />}
					/>
					<Route
						path="/apps/geo-master-quest"
						element={<Navigate to="/apps/geo-quiz-guess-the-flag/" replace />}
					/>
					<Route path="/apps/plotto" element={<PlottoGamePage />} />
					<Route
						path="/projects/plotto"
						element={<PlottoGamePage />}
					/>
					<Route
						path="/apps/echo-match"
						element={<ProductLandingPage slug="echo-match" />}
					/>
					<Route
						path="/projects/echo-match"
						element={<ProductLandingPage slug="echo-match" />}
					/>
					<Route
						path="/projects/quiet-vault"
						element={
							<ExternalProjectRedirect
								title="QuietVault"
								url="https://quietvaultapp.com/"
							/>
						}
					/>
					<Route
						path="/projects/quietvault"
						element={
							<ExternalProjectRedirect
								title="QuietVault"
								url="https://quietvaultapp.com/"
							/>
						}
					/>
					<Route
						path="/projects/lunexa-messages"
						element={
							<ExternalProjectRedirect
								title="Lunexa Messages"
								url="https://lunexamessages.com/"
							/>
						}
					/>
					<Route path="/contact" element={<Contact />} />
					<Route path="/privacy" element={<PrivacyPolicy />} />
					<Route
						path="/privacy/lunexa-messages"
						element={<LunexaMessagesPrivacyPolicy />}
					/>
					<Route
						path="/terms/lunexa-messages"
						element={<LunexaMessagesTerms />}
					/>
					<Route
						path="/privacy/quiet-vault"
						element={<QuietVaultPrivacyPolicy />}
					/>
					<Route
						path="/terms/quiet-vault"
						element={<QuietVaultTerms />}
					/>
					<Route path="/ads" element={<SkadNetworkMerge />} />
					<Route path="/appads" element={<AppAdsTxtMerger />} />
					<Route path="*" element={<Notfound />} />
				</Routes>
			</Suspense>
		</div>
	);
}

export default App;
